import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type Rec } from '../lib/api';
import { keys } from '../lib/queries';

/**
 * Отмена держится на клиенте: сервер пишет историю, но эндпоинта «откатить
 * последнее действие» у него нет. Поэтому вместе с каждой правкой мы
 * запоминаем обратную операцию и при Ctrl+Z проигрываем её.
 *
 * Удалённые строки восстанавливаются с новыми id — старые уже не вернуть.
 */
type Action =
  | { kind: 'patch'; label: string; before: Rec[]; after: Rec[] }
  | { kind: 'create'; label: string; ids: string[]; fields: Record<string, unknown>[]; afterId?: string }
  | { kind: 'delete'; label: string; fields: Record<string, unknown>[] };

const LIMIT = 50;

export function useUndoableRecords(docId: string, tableId: string) {
  const qc = useQueryClient();

  const undoStack = useRef<Action[]>([]);
  const redoStack = useRef<Action[]>([]);
  const [depth, setDepth] = useState<{
    canUndo: number;
    canRedo: number;
    undoLabel?: string;
    redoLabel?: string;
  }>({ canUndo: 0, canRedo: 0 });
  const [busy, setBusy] = useState(false);

  // Стек привязан к листу: при переключении начинаем с чистого листа.
  useEffect(() => {
    undoStack.current = [];
    redoStack.current = [];
    setDepth({ canUndo: 0, canRedo: 0 });
  }, [tableId]);

  const sync = () =>
    setDepth({
      canUndo: undoStack.current.length,
      canRedo: redoStack.current.length,
      // Подпись верхнего действия — её показывает подсказка кнопки.
      undoLabel: undoStack.current[undoStack.current.length - 1]?.label,
      redoLabel: redoStack.current[redoStack.current.length - 1]?.label,
    });

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: keys.records(docId, tableId) });
    qc.invalidateQueries({ queryKey: ['history', docId, tableId] });
  }, [qc, docId, tableId]);

  const remember = (action: Action) => {
    undoStack.current.push(action);
    if (undoStack.current.length > LIMIT) undoStack.current.shift();
    redoStack.current = [];
    sync();
  };

  const currentRecords = () => qc.getQueryData<Rec[]>(keys.records(docId, tableId)) ?? [];

  /** Правка ячеек: запоминаем прежние значения только по затронутым полям. */
  const patch = useCallback(
    async (records: Rec[], actionLabel = 'Изменение') => {
      const snapshot = currentRecords();
      const before: Rec[] = records.map((r) => {
        const prev = snapshot.find((x) => x.id === r.id);
        const fields: Record<string, unknown> = {};
        for (const key of Object.keys(r.fields)) fields[key] = prev?.fields[key] ?? null;
        return { id: r.id, fields };
      });

      await api.patchRecords(docId, tableId, records);
      remember({ kind: 'patch', label: actionLabel, before, after: records });
      refresh();
    },
    [docId, tableId, refresh],
  );

  const create = useCallback(
    async (rows: { fields: Record<string, unknown> }[], afterId?: string) => {
      const res = await api.createRecords(docId, tableId, rows, afterId);
      remember({
        kind: 'create',
        label: 'Создание',
        ids: res.records.map((r) => r.id),
        fields: rows.map((r) => r.fields),
        afterId,
      });
      refresh();
      return res;
    },
    [docId, tableId, refresh],
  );

  const remove = useCallback(
    async (ids: string[]) => {
      const snapshot = currentRecords();
      const fields = ids
        .map((id) => snapshot.find((r) => r.id === id)?.fields)
        .filter(Boolean) as Record<string, unknown>[];

      const res = await api.deleteRecords(docId, tableId, ids);
      remember({ kind: 'delete', label: 'Удаление', fields });
      refresh();
      return res;
    },
    [docId, tableId, refresh],
  );

  const apply = useCallback(
    async (action: Action, direction: 'undo' | 'redo'): Promise<Action> => {
      switch (action.kind) {
        case 'patch': {
          const target = direction === 'undo' ? action.before : action.after;
          await api.patchRecords(docId, tableId, target);
          return action;
        }
        case 'create': {
          if (direction === 'undo') {
            await api.deleteRecords(docId, tableId, action.ids);
            return action;
          }
          const res = await api.createRecords(
            docId,
            tableId,
            action.fields.map((fields) => ({ fields })),
            action.afterId,
          );
          return { ...action, ids: res.records.map((r) => r.id) };
        }
        case 'delete': {
          if (direction === 'undo') {
            const res = await api.createRecords(
              docId,
              tableId,
              action.fields.map((fields) => ({ fields })),
            );
            // Для повторного удаления нужны новые id.
            return {
              kind: 'create',
              label: action.label,
              ids: res.records.map((r) => r.id),
              fields: action.fields,
            };
          }
          return action;
        }
      }
    },
    [docId, tableId],
  );

  const undo = useCallback(async () => {
    const action = undoStack.current.pop();
    if (!action || busy) return;
    setBusy(true);
    try {
      const applied = await apply(action, 'undo');
      redoStack.current.push(applied);
    } finally {
      setBusy(false);
      sync();
      refresh();
    }
  }, [apply, busy, refresh]);

  const redo = useCallback(async () => {
    const action = redoStack.current.pop();
    if (!action || busy) return;
    setBusy(true);
    try {
      const applied = await apply(action, 'redo');
      undoStack.current.push(applied);
    } finally {
      setBusy(false);
      sync();
      refresh();
    }
  }, [apply, busy, refresh]);

  // Ctrl+Z и Ctrl+Shift+Z / Ctrl+Y — как в бандле.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      if (!(e.ctrlKey || e.metaKey)) return;

      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return { patch, create, remove, undo, redo, busy, ...depth };
}
